// @ts-strict-ignore
import * as React from 'react';
import kebabCase from 'lodash/kebabCase';
import {clsx} from 'clsx';

import {useTranslation} from 'common/i18n/hooks';
import {FilterPartsProps} from 'common/query-builder/query-entry-section/row-types/filter/parts/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {
  BooleanOperator,
  FilterOperator,
  FilterValue,
  ListQuantifier,
  SCDOptionsType,
} from 'common/widgets/property-filter-menu/models/types';
import {
  applyFilterOperatorToFilter,
  getDurationFilterUnitLabel,
  getFilterOperatorsSections,
  getFilterOperatorSymbol,
  getListQuantifierSections,
} from 'common/query-builder/query-entry-section/row-types/filter/utils';
import {hasComputedListFilters, isEnhancedListFilter} from 'common/widgets/property-filter-menu/utils';
// eslint-disable-next-line import/no-cycle -- value component can render filter parts recursively
import {
  FilterListValueTemplate,
  FilterValueTemplate,
} from 'common/query-builder/query-entry-section/row-types/filter/types/index';
import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {Placement} from 'common/components/tooltip/types';
import {ChangeEvent} from 'common/components/select/types';
import {SectionItem} from 'common/components/items-menu/types';
import {TypecastSubitem} from 'common/query-builder/query-entry-section/components/typecast-subitem';
import {DURATION_UNIT_OPTION_SECTION_ITEMS} from 'common/query-builder/query-block/properties-menu/constants';
import {ProfileHistorySubItem} from 'common/query-builder/query-entry-section/components/profile-history-subitem';
import {SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {DatePickerMode} from 'common/widgets/date-range-picker-new/util';
import {DateRange, DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {isExperimentPropertyName, isScdPropertyName} from 'common/report/util/properties';
import {TimeQueryBlock} from 'common/query-builder/query-block/time-menu/time-query-block';
import {getSCDOptionsTypeSections} from 'common/query-builder/query-entry-section/row-types/segment-entry/components/segment-scd-options-selector/index';
import {Segmentation} from 'common/types/reports/query';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatDate} from 'common/util/date';
import {SelectorType} from 'common/query-builder/query-block/constants';

function parseScdDateRange(scdOptions: Segmentation[`scdOptions`]): DateRangeAttributes {
  const type = scdOptions?.type ?? `latest`;
  let dateRange = scdOptions?.dateRange;
  const date = dateRange?.to ?? formatDate(new Date(), {iso: true});
  if (type === `latest` && dateRange.type !== DateRangeType.On) {
    dateRange = {
      type: DateRangeType.On,
      from: date,
      to: date,
    };
  } else if (type === `any` && (!dateRange || dateRange.type === DateRangeType.On)) {
    dateRange = {
      type: DateRangeType.RelativeAfter,
      window: {
        unit: TimeUnit.Week,
        value: 4,
      },
    };
  }
  return dateRange;
}

/** Returns validated SCD options.
 *
 * SCD options with `latest` type should have a date range of type `on`, while
 * `any` type should have a date range of type `between` and should span more
 * than a day. This function ensures these conditions are met, and defaults to a
 * valid date range if the input is invalid.
 */
function scdOptionsWithUpdatedDateRange(scdOptions: Segmentation[`scdOptions`]) {
  if (!scdOptions?.dateRange && scdOptions.type === `latest`) {
    return scdOptions;
  }
  return {
    ...scdOptions,
    dateRange: parseScdDateRange(scdOptions),
  };
}

// defines which filter types do not render an operator selector
// this is a temporal check until we can bring all the new filter
// UI to all filter types
const NO_OPERATOR_TYPES = new Set<PropertyType>([PropertyType.Object, PropertyType.NonProperty]);

export function FilterParts(props: FilterPartsProps) {
  const {print} = useTranslation();

  const hasProperty = props.filter.hasSelectedProperty();
  const isCohortFilter = props.filter.resourceType === ResourceType.Cohort;

  const changeOperator = (operator: FilterOperator) => {
    const newFilter = applyFilterOperatorToFilter(operator, props.filter);

    props.updateEntry(newFilter);
    props.onChangeOperator?.(operator);
  };

  const changeValue = (value: FilterValue) => {
    if (props.filter.propertyType === PropertyType.Boolean) {
      // changing the operator for boolean values also handles changing the value
      changeOperator(value ? BooleanOperator.True : BooleanOperator.False);
    } else {
      props.updateEntry(props.filter.setAttribute(`filterValue`, value));
    }

    props.onChangeValue?.(value);
  };

  const showListQuantifier =
    isEnhancedListFilter(props.filter) && !props.hideListQuantifier && !hasComputedListFilters(props.filter);

  const FilterValueComponent =
    (isEnhancedListFilter(props.filter) && FilterListValueTemplate[props.filter.propertyType]) ||
    FilterValueTemplate[props.filter.propertyType];

  const scdOptionsType = props.filter.scdOptions ? props.filter.scdOptions.type ?? `latest` : null;
  const showDateConfig =
    isScdPropertyName(props.filter.propertyName) || isExperimentPropertyName(props.filter.propertyName);

  return (
    <div
      className={clsx(`mp-query-entry-filter-parts`, {
        'with-quick-delete': props.withQuickDelete,
        'with-quick-rename': props.withQuickRename,
      })}
    >
      {isScdPropertyName(props.filter.propertyName) && (
        <div className='mp-query-entry-filter-parts-row'>
          <SelectReact
            elref='scdTypeQuantifier'
            menuWidth={200}
            alwaysShowCaret={props.hasNewUI}
            theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
            tooltip={{placement: Placement.Top}}
            readonly={props.readonly}
            sections={getSCDOptionsTypeSections(props.filter?.scdOptions)}
            onChange={({detail}: ChangeEvent<SectionItem<SCDOptionsType>>) => {
              const updatedSCDOptions = {
                ...props?.filter?.scdOptions,
                type: detail.item.value as any as Segmentation[`scdOptions`][`type`],
              };
              props.updateEntry(
                props.filter.setAttribute(
                  `scdOptions`,
                  props.disableScdDateRange ? updatedSCDOptions : scdOptionsWithUpdatedDateRange(updatedSCDOptions),
                ),
              );
            }}
          />
        </div>
      )}
      {/** enhanced list property filters define a quantifier operator for items in the list */}
      {showListQuantifier && (
        <div className='mp-query-entry-filter-parts-row'>
          <SelectReact
            elref='listFilterQuantifier'
            className='list-filter-quantifier'
            menuWidth={200}
            alwaysShowCaret={props.hasNewUI}
            theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
            tooltip={{placement: Placement.Top}}
            readonly={props.readonly}
            sections={getListQuantifierSections(props.filter)}
            onChange={({detail}: ChangeEvent<SectionItem<ListQuantifier>>) => {
              props.updateEntry(props.filter.setAttribute(`listQuantifier`, detail.item.value));
            }}
          />
        </div>
      )}

      {showDateConfig && !props.hasNewUI && scdOptionsType && props.filter.scdOptions?.dateRange && (
        <>
          {scdOptionsType === `latest` && (
            <SelectReact
              disableSearch
              menuWidth={200}
              menuHeight={0}
              readonly
              theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
              sections={[
                {
                  items: [
                    {
                      value: `As Of`,
                      label: print(`As of`),
                      isSelected: true,
                    },
                  ],
                },
              ]}
              neverShowCaret
              disableTooltip
            />
          )}
          <TimeQueryBlock
            className='subitem-style'
            theme={ButtonTheme.Select}
            buttonSize={ButtonSize.Medium}
            selected={parseScdDateRange(props.filter.scdOptions)}
            datePickerModesOption={
              scdOptionsType === `latest`
                ? [DatePickerMode.On]
                : [DatePickerMode.Between, DatePickerMode.Since, DatePickerMode.InTheLast]
            }
            selectorType={scdOptionsType === `latest` ? SelectorType.Text : null}
            singlePickerMode={scdOptionsType === `latest`}
            leftIcon='calendar'
            onSelect={(dateRange: DateRange) => {
              props.updateEntry(
                props.filter.setAttribute(
                  `scdOptions`,
                  scdOptionsWithUpdatedDateRange({
                    ...props.filter.scdOptions,
                    dateRange: dateRange.getAttributes(),
                  }),
                ),
              );
            }}
          />
        </>
      )}

      <div className='mp-query-entry-filter-parts-row'>
        {hasProperty &&
          !isCohortFilter &&
          !NO_OPERATOR_TYPES.has(props.filter.propertyType) &&
          // newUI uses a radio-button selection specified in FilterValueTemplate for boolean values instead of a SelectEl
          !(props.hasNewUI && props.filter.propertyType === PropertyType.Boolean) && (
            <SelectReact
              elref='filterOperator'
              className={clsx(
                `for-${props.filter.propertyType}`,
                `for-${kebabCase(props.filter.filterOperator)}-operator`,
              )}
              disableSearch
              menuWidth={200}
              menuHeight={0}
              readonly={props.readonly}
              selectedOption={print(
                props.operatorOverrides?.find(({value}) => value === props.filter.filterOperator)?.label ||
                  getFilterOperatorSymbol(props.filter),
              )}
              theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
              neverShowCaret
              disableTooltip
              sections={getFilterOperatorsSections(props.filter, props.operatorOverrides)}
              onChange={(ev: ChangeEvent) => changeOperator(ev.detail.item.value[0])}
            />
          )}

        {/** render value selection only for non-cohort filters */}
        {!isCohortFilter && FilterValueComponent && (
          <FilterValueComponent
            filter={props.filter}
            eventNames={props.eventNames}
            dataGroupId={props.dataGroupId}
            projectId={props.projectId}
            workspaceId={props.workspaceId}
            userId={props.userId}
            readonly={props.readonly}
            disabled={props.valueDisabled}
            tooltip={props.valueTooltip}
            hasNewUI={props.hasNewUI}
            rowActions={props.rowActions}
            hasPlaceholder={props.hasPlaceholder}
            isActive={props.isValueActive}
            onChange={(value) => changeValue(value)}
            onSelectMenuChange={(ev) => props.onValueSelectMenuChange?.(ev)}
            setActiveEntry={(state) => props.setActiveEntry?.(state)}
            updateEntry={(filter) => props.updateEntry(filter)}
            isTimePickerEnabled={props.isTimePickerEnabled}
            setTimePickerEnabled={props.setTimePickerEnabled}
          />
        )}

        {FilterValueComponent && props.filter.supportsFilterUnit() && (
          <SelectReact
            elref='filterUnit'
            disableSearch
            menuWidth={200}
            menuHeight={0}
            readonly={props.readonly}
            selectedOption={getDurationFilterUnitLabel(props.filter)}
            theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
            neverShowCaret
            disableTooltip
            className={clsx(
              `for-${props.filter.propertyType}`,
              `for-${kebabCase(props.filter.filterOperator)}-operator`,
            )}
            sections={[{items: DURATION_UNIT_OPTION_SECTION_ITEMS}]}
            onChange={(ev: ChangeEvent) =>
              props.updateEntry(props.filter.setAttribute(`filterDateUnit`, ev.detail.item.value))
            }
          />
        )}

        {props.unit && <div className='filter-value-unit'>{props.unit}</div>}
      </div>

      {props.hasNewUI && props.showTypecastControl && (
        <TypecastSubitem
          value={props.filter}
          onTypeChange={props.updateEntry}
          disabledTypes={props.disabledTypecastTypes}
          forFilter
        />
      )}

      {hasProperty && props.isProfileHistoryEnabled && props.filter.resourceType === ResourceType.User && (
        <ProfileHistorySubItem
          hasNewUI={props.hasNewUI}
          value={props.filter}
          onTypeChange={props.updateEntry}
          hideEvents={props.hideEvents}
          selectorType={SelectorTypeDefinitions.Filter}
        />
      )}
    </div>
  );
}
